import * as React from "react"

import { SearchInput } from "@/components/ui/search-input"
import { FilterButton } from "@/components/ui/table-filters"
import { SortButton, type SortOption } from "@/components/ui/table-sort"
import type { FilterDef, FilterValues, TableSort } from "@/lib/table"

export interface TableToolbarProps {
  search: string
  onSearchChange: (v: string) => void
  searchPlaceholder?: string
  searchAriaLabel?: string
  searchRef?: React.RefObject<HTMLInputElement>
  searchSuffix?: React.ReactNode

  filterSchema: FilterDef[]
  filterValues: FilterValues
  onFiltersChange: (v: FilterValues) => void
  onClearAllFilters: () => void

  sortOptions: SortOption[]
  sort: TableSort | null
  onSortChange: (v: TableSort | null) => void
}

/**
 * Search left, Filter + Sort right — the shape every table screen uses. Wraps
 * on narrow viewports so search stays full-width.
 */
export function TableToolbar({
  search,
  onSearchChange,
  searchPlaceholder,
  searchAriaLabel,
  searchRef,
  searchSuffix,
  filterSchema,
  filterValues,
  onFiltersChange,
  onClearAllFilters,
  sortOptions,
  sort,
  onSortChange,
}: TableToolbarProps): JSX.Element {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div className="relative min-w-0 flex-1 sm:max-w-[440px]">
        <SearchInput
          ref={searchRef}
          value={search}
          onChange={(e) => onSearchChange(e.currentTarget.value)}
          onClear={() => onSearchChange("")}
          placeholder={searchPlaceholder}
          aria-label={searchAriaLabel ?? searchPlaceholder}
          aria-keyshortcuts={searchSuffix ? "/" : undefined}
        />
        {searchSuffix && (
          <span
            aria-hidden="true"
            className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2"
          >
            {searchSuffix}
          </span>
        )}
      </div>
      <div className="flex shrink-0 items-center gap-2">
        <FilterButton
          schema={filterSchema}
          values={filterValues}
          onChange={onFiltersChange}
          onClearAll={onClearAllFilters}
        />
        <SortButton options={sortOptions} value={sort} onChange={onSortChange} />
      </div>
    </div>
  )
}
